import React, { useRef, useState } from 'react';
import Script from 'next/script';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { getTypeFormMime } from '@/utils/uploadMedia/getTypeFromMime';
import { getUploadedMedia } from '@/utils/uploadMedia/uploadHelpers/getUploadedMedia';
import { uploadMedia } from '@/utils/uploadMedia/uploadHelpers/uploadMedia';
import faqUrl from '@/utils/urls/faqUrl';

// Endpoints
import { CREATE_MEDIA } from '@/lib/routes/apiEndpoints';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    THREADS,
    TIKTOK_BUSINESS,
    TWITTER
} from '@/lib/socialProfile';
// Lib
import { url_faq_media_library_add } from '@/lib/urls/faqUrls';

// Components
import Dropdown from '@/components/base/Dropdown';
import InfoBanner from '@/components/base/InfoBanner';
import LinkBase from '@/components/base/LinkBase';
import Loader from '@/components/base/Loader';
import Modal from '@/components/base/Modal';
import { Trans } from '@/components/tools/translation';

const defaultFileExtensions = [
    '.png',
    '.jpeg',
    '.jpg',
    '.avi',
    '.mp4',
    '.3gp',
    '.3g2',
    '.webm',
    '.mkv',
    '.m2v',
    '.m4v',
    '.mov'
];

const acceptedExtensions = {
    [INSTAGRAM]: defaultFileExtensions,
    [FACEBOOK]: defaultFileExtensions,
    [TWITTER]: [...defaultFileExtensions, '.gif', '.webp'],
    [LINKEDIN]: ['.png', '.jpeg', '.jpg', '.gif', '.mp4', '.ppt', '.pptx', '.pdf', '.doc', '.docx'],
    [TIKTOK_BUSINESS]: ['.mp4', '.mov', '.webm', '.jpeg', '.jpg', '.webp'],
    [PINTEREST]: ['.png', '.jpeg', '.jpg', '.mp4', '.m4v', '.mov'],
    [THREADS]: defaultFileExtensions
};

const listItemStyle = {
    base: 'p-2 hover:bg-icoGray-300 hover:dark:bg-icoDarkMode-goudron',
    small: 'text-xs text-icoBlack dark:text-white p-2 hover:bg-icoBlue-100 hover:text-icoBlue dark:bg-icoBlue/20'
};

function getFileExtension(mimeType) {
    return mimeType.split('/')[1];
}

function getTransform(mimeType) {
    const media_type = getTypeFormMime(mimeType);
    const fileExtension = getFileExtension(mimeType);

    if (media_type === 'image' && (fileExtension !== 'jpeg' || fileExtension !== 'jpg')) {
        return 'convert';
    }
    if (media_type === 'video' && fileExtension !== 'mp4') {
        return 'transcode';
    }
    return undefined;
}

export const MediaUpload = ({
    mutateLibraryMedia,
    button,
    classNameWrapper = '',
    dropdownSize = 'base',
    dropdownPosition = 'base',
    itemStyle = 'base'
}) => {
    const modalOpenRef = useRef(false);
    const [cancelTokens, setCancelTokens] = useState([]);
    const { t } = useTranslation('media');
    const { activeSocialProfile } = useActiveSocialProfile();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [progress, setProgress] = useState({});
    const [filesErrors, setFilesErrors] = useState([]);

    const hiddenFileInput = React.useRef(null);
    const handleClick = () => {
        hiddenFileInput.current.click();
    };

    const handleOnFileChange = async (target) => {
        const files = target.files || target;
        const errors = [];
        setFilesErrors([]);
        setProgress({
            total: files.length,
            current: 0
        });
        modalOpenRef.current = true;
        setIsUploadModalOpen(true);
        for (let index = 0; index < files.length; index++) {
            let file = files[index];
            if (!(file instanceof File)) {
                try {
                    // files[index]?.link for dropbox and files[index]['@microsoft.graph.downloadUrl'] for onedrive
                    if (file?.link || file['@microsoft.graph.downloadUrl']) {
                        let response = await fetch(
                            file?.link ?? file['@microsoft.graph.downloadUrl']
                        );
                        let data = await response.blob();
                        file = new File([data], file.name, { type: data?.type });
                    }
                } catch (e) {
                    errors.push({
                        filename: file.name,
                        message: 'error_occured_create_media'
                    });
                    file = null;
                }
            }

            let isUploaded = false;
            if (file) {
                try {
                    // Add cancel token to the list
                    const cancelToken = new AbortController();
                    setCancelTokens((prevState) => [...prevState, cancelToken]);

                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('folder', 'media_library');
                    formData.append('upload_type', 'library');
                    formData.append('social_network', activeSocialProfile?.social_network_type);

                    const media_type = getTypeFormMime(file.type);
                    // TODO: Remove auto convert and transcode when new scheduler is up
                    const transform = getTransform(file.type);
                    let dataToUpload = {
                        file,
                        media_type
                    };
                    if (transform) {
                        if (transform === 'convert') {
                            dataToUpload.transform = 'convert';
                            dataToUpload.convert = 'jpeg';
                        }
                        if (transform === 'transcode') {
                            dataToUpload.transform = 'transcode';
                            dataToUpload.transcode = 'default';
                        }
                    }
                    const data = await uploadMedia({
                        dataToUpload,
                        cancelToken
                    });
                    const uploadedMedia = await getUploadedMedia({
                        media_id: data.media_id,
                        cancelToken
                    });

                    let scheduler_media_id = uploadedMedia.id;

                    if (transform) {
                        const transformedMedia = uploadedMedia.children[0];
                        scheduler_media_id = transformedMedia.id;
                    }

                    await post(
                        injectEndpointUrlParams(CREATE_MEDIA, {
                            SPID: activeSocialProfile.id
                        }),
                        { scheduler_media_id }
                    );
                } catch (e) {
                    if (isUploaded) {
                        errors.push({
                            filename: file.name,
                            message: 'error_occured_create_media'
                        });
                    } else {
                        if (e.response?.data?.error) {
                            errors.push({
                                filename: file.name,
                                message: e.response.data.error,
                                details: true
                            });
                        } else if (e.response?.data?.errors?.file?.length > 0) {
                            errors.push({
                                filename: file.name,
                                message: `error_global_media_${e.response.data.errors.file[0]}`,
                                details: true
                            });
                        } else {
                            errors.push({
                                filename: file.name,
                                message: 'error_global_media_checking',
                                details: true
                            });
                        }
                    }
                }
            }

            setProgress((prevProgress) => ({
                ...prevProgress,
                current: index + 1
            }));
        }

        target.value = null;
        mutateLibraryMedia();
        if (errors.length > 0) {
            setFilesErrors(errors);
        } else {
            modalOpenRef.current = false;
            setIsUploadModalOpen(false);
        }
    };

    const launchOneDrivePicker = (
        multiSelect = true,
        openInNewWindow = true,
        advancedOptions = {},
        action = 'download',
        onSuccess = null,
        onCancel = null,
        onError = null
    ) => {
        const options = {
            clientId: process.env.NEXT_PUBLIC_ONE_DRIVE_CLIENT_ID,
            action: action,
            multiSelect: multiSelect,
            openInNewWindow: openInNewWindow,
            advanced: advancedOptions,
            success: (files) => {
                onSuccess && onSuccess(files);
            },
            cancel: () => {
                onCancel && onCancel();
            },
            error: (e) => {
                onError && onError(e);
            }
        };

        OneDrive.open(options);
    };

    const launchDropboxPicker = (
        extensions = [],
        multiSelect = true,
        onSuccess = null,
        onCancel = null
    ) => {
        const options = {
            // Required. Called when a user selects an item in the Chooser.
            success: (files) => {
                onSuccess && onSuccess(files);
            },

            // Optional. Called when the user closes the dialog without selecting a file
            // and does not include any parameters.
            cancel: () => {
                onCancel && onCancel();
            },

            // Optional. "preview" (default) is a preview link to the document for sharing,
            // "direct" is an expiring link to download the contents of the file. For more
            // information about link types, see Link types below.
            linkType: 'dm', // or "direct"

            // Optional. A value of false (default) limits selection to a single file, while
            // true enables multiple file selection.
            multiselect: multiSelect, // or true

            // Optional. This is a list of file extensions. If specified, the user will
            // only be able to select files with these extensions. You may also specify
            // file types, such as "video" or "images" in the list. For more information,
            // see File types below. By default, all extensions are allowed.
            extensions: extensions,

            // Optional. A value of false (default) limits selection to files,
            // while true allows the user to select both folders and files.
            // You cannot specify `linkType: "direct"` when using `folderselect: true`.
            folderselect: false // or true
        };

        Dropbox.choose(options);
    };

    return (
        <>
            <input
                type="file"
                multiple
                accept={acceptedExtensions?.[activeSocialProfile?.social_network_type]?.join(', ')}
                ref={hiddenFileInput}
                onChange={(e) => handleOnFileChange(e.target)}
                className="hidden"
            />
            <Dropdown
                button={button}
                size={dropdownSize}
                position={dropdownPosition}
                classNameWrapper={classNameWrapper}>
                <Dropdown.Item className="flex px-1 py-1 text-base" onClick={handleClick}>
                    <button
                        type="button"
                        className={clsx('w-full text-left', listItemStyle[itemStyle])}>
                        {t('media_computer')}
                    </button>
                </Dropdown.Item>
                <Dropdown.Item className="flex px-1 pb-1 text-base">
                    <button
                        type="button"
                        className={clsx('w-full text-left', listItemStyle[itemStyle])}
                        onClick={() =>
                            launchDropboxPicker(
                                acceptedExtensions[activeSocialProfile.social_network_type],
                                true,
                                (result) => {
                                    handleOnFileChange(result);
                                }
                            )
                        }>
                        {t('media_dropbox')}
                    </button>
                </Dropdown.Item>
                <Dropdown.Item className="flex px-1 pb-1 text-base">
                    <button
                        type="button"
                        className={clsx('w-full text-left', listItemStyle[itemStyle])}
                        onClick={() =>
                            launchOneDrivePicker(
                                true,
                                true,
                                {
                                    filter:
                                        'folder,' +
                                        acceptedExtensions[
                                            activeSocialProfile.social_network_type
                                        ].join(',')
                                },
                                'download',
                                (result) => {
                                    handleOnFileChange(result.value);
                                },
                                null,
                                () => {
                                    modalOpenRef.current = true;
                                    setIsUploadModalOpen(true);
                                    setFilesErrors([
                                        {
                                            message: 'error_global_one_drive_upload'
                                        }
                                    ]);
                                }
                            )
                        }>
                        {t('media_onedrive')}
                    </button>
                </Dropdown.Item>
            </Dropdown>
            <Modal
                open={isUploadModalOpen}
                overlayClosable={false}
                onDismiss={() => {
                    modalOpenRef.current = false;
                    hiddenFileInput.current.value = '';
                    setIsUploadModalOpen(false);
                    if (cancelTokens.length > 0) {
                        cancelTokens.forEach((cancelToken) => cancelToken.abort());
                        setCancelTokens([]);
                    }
                }}>
                <Modal.Body className="pt-0">
                    <Modal.Content className="p-8">
                        {filesErrors.length > 0 ? (
                            <div className="flex flex-col space-y-4">
                                {filesErrors.map((error, index) => (
                                    <InfoBanner
                                        key={index}
                                        title={
                                            error?.filename
                                                ? t(
                                                      'errors:error_media_library.add_new_media.error_occured_with_file',
                                                      { file: error.filename }
                                                  )
                                                : ''
                                        }
                                        message={t(
                                            `errors:error_media_library.add_new_media.${error.message}`
                                        )}>
                                        {error.details && (
                                            <Trans
                                                i18nKey={`errors:error_media_library.add_new_media.error_details`}
                                                components={{
                                                    faqLink: (
                                                        <LinkBase
                                                            key="faq"
                                                            className="underline"
                                                            href={faqUrl(url_faq_media_library_add)}
                                                            target="_blank"
                                                            rel="help noopener noreferrer"
                                                        />
                                                    )
                                                }}
                                            />
                                        )}
                                    </InfoBanner>
                                ))}
                            </div>
                        ) : (
                            <div className="flex text-icoGray-500 dark:text-icoDarkMode-wolf">
                                <Loader className="mr-3 h-5 w-5 text-icoBlue" />
                                <p>
                                    {t('upload_in_progress')} {progress.current}/{progress.total}{' '}
                                    {t(progress.total > 0 ? 'uploaded_one' : 'uploaded_several')}
                                </p>
                            </div>
                        )}
                    </Modal.Content>
                </Modal.Body>
            </Modal>
            <Script
                id="one-drive"
                strategy="lazyOnload"
                src="https://js.live.net/v7.0/OneDrive.js"
            />
            <Script
                strategy="lazyOnload"
                src="https://www.dropbox.com/static/api/2/dropins.js"
                id="dropboxjs"
                data-app-key={process.env.NEXT_PUBLIC_DROPBOX_CLIENT_ID}
            />
        </>
    );
};

export default MediaUpload;
